import { styled } from '@linaria/react';
import { ActionIcon, Anchor, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useAffectedItems } from '../../api/insights-client/insights-client.hooks';
import { useTranslation } from 'react-i18next';
import { resolveTaskTypeDisplayText } from '../../helpers/task-type-labels/task-type-labels';
import { baseWhite, blueBase, inkDark, inkLight, purpleBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { formatDateWithFallback, getRiskScoreColor } from './insights-helpers';

type AffectedItemsSidebarProps = {
  insightId: string | null;
  onClose: () => void;
};

export function AffectedItemsSidebar({ insightId, onClose }: AffectedItemsSidebarProps) {
  const i18n = useTranslation();
  const { affectedItems, query } = useAffectedItems(insightId ?? undefined);

  if (!insightId) {
    return null;
  }

  return (
    <SidebarContainer>
      <Header>
        <TitleRow>
          <Group gap="xs" align="center">
            <Icon name="info" color={purpleBase} variant="filled" size={24} />
            <Text size="lg" fw={600} c={inkDark}>
              Affected Items
            </Text>
          </Group>
          <ActionIcon variant="subtle" onClick={onClose} size="sm">
            <Icon name="close" color={inkLight} variant="filled" size={20} />
          </ActionIcon>
        </TitleRow>

        <ItemCountRow>
          <Text size="lg" fw={600} c={inkDark}>
            {query.isLoading ? 'Loading...' : affectedItems ? `${affectedItems.total_count} Items` : '0 Items'}
          </Text>
        </ItemCountRow>
      </Header>

      <ScrollArea h="calc(100vh - 180px)">
        <ItemsContainer>
          {query.isLoading ? (
            <EmptyState>
              <Icon name="hourglass_empty" color={inkLight} variant="filled" size={48} />
              <Text size="sm" c={inkLight} fw={500}>
                Loading affected items...
              </Text>
            </EmptyState>
          ) : !affectedItems || affectedItems.affected_items.length === 0 ? (
            <EmptyState>
              <Icon name="inbox" color={inkLight} variant="filled" size={48} />
              <Text size="sm" c={inkLight} fw={500}>
                No affected items found
              </Text>
            </EmptyState>
          ) : (
            affectedItems.affected_items.map((item) => (
              <ItemCard key={item.id}>
                <Stack gap={12}>
                  {/* Code/ID */}
                  {item.code && (
                    <Text size="xs" fw={600} c={blueBase}>
                      {item.code}
                    </Text>
                  )}

                  {/* Title/Name */}
                  {item.url ? (
                    <Anchor href={item.url} target="_blank" rel="noopener noreferrer" size="sm" fw={600} c={inkDark}>
                      {item.name}
                    </Anchor>
                  ) : (
                    <Text size="sm" fw={600} c={inkDark}>
                      {item.name}
                    </Text>
                  )}

                  {/* Created Date */}
                  {item.created_at && (
                    <MetadataRow>
                      <MetadataLabel>Created</MetadataLabel>
                      <MetadataValue>{formatDateWithFallback(item.created_at)}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Type */}
                  {item.type && (
                    <MetadataRow>
                      <MetadataLabel>Type</MetadataLabel>
                      <MetadataValue>{resolveTaskTypeDisplayText(item) ?? item.type}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Status or State */}
                  {(item.status || item.state) && (
                    <MetadataRow>
                      <MetadataLabel>{item.state ? 'State' : 'Status'}</MetadataLabel>
                      <MetadataValue>{item.status || item.state}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Estimate/Points */}
                  {item.estimate !== undefined && (
                    <MetadataRow>
                      <MetadataLabel>Points</MetadataLabel>
                      <MetadataValue>{item.estimate || '-'}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Project */}
                  {item.project_name && (
                    <MetadataRow>
                      <MetadataLabel>Project</MetadataLabel>
                      <MetadataValue>{item.project_name}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Assignee */}
                  {item.assignee_name && (
                    <MetadataRow>
                      <MetadataLabel>Assignee</MetadataLabel>
                      <MetadataValue>{item.assignee_name}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Epic */}
                  {item.epic_name && (
                    <MetadataRow>
                      <MetadataLabel>{i18n.t('common.epic')}</MetadataLabel>
                      <MetadataValue>{item.epic_name}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Repository (for PRs) */}
                  {item.repository_name && (
                    <MetadataRow>
                      <MetadataLabel>Repository</MetadataLabel>
                      <MetadataValue>{item.repository_name}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Author (for PRs) */}
                  {item.author_name && (
                    <MetadataRow>
                      <MetadataLabel>Author</MetadataLabel>
                      <MetadataValue>{item.author_name}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Start/End Dates (for Initiatives) */}
                  {item.start_date && (
                    <MetadataRow>
                      <MetadataLabel>Start Date</MetadataLabel>
                      <MetadataValue>{formatDateWithFallback(item.start_date)}</MetadataValue>
                    </MetadataRow>
                  )}
                  {item.end_date && (
                    <MetadataRow>
                      <MetadataLabel>End Date</MetadataLabel>
                      <MetadataValue>{formatDateWithFallback(item.end_date)}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* Risk Score */}
                  {item.risk_score !== undefined && (
                    <MetadataRow>
                      <MetadataLabel>Risk Score</MetadataLabel>
                      <Group gap="xs" align="center" style={{ flex: 1, justifyContent: 'flex-end' }}>
                        <RiskScoreBar>
                          <RiskScoreFill score={item.risk_score} color={getRiskScoreColor(item.risk_score)} />
                        </RiskScoreBar>
                        <MetadataValue>{item.risk_score}/10</MetadataValue>
                      </Group>
                    </MetadataRow>
                  )}
                </Stack>
              </ItemCard>
            ))
          )}
        </ItemsContainer>
      </ScrollArea>
    </SidebarContainer>
  );
}

const SidebarContainer = styled.div`
  background: ${baseWhite};
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemCountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemsContainer = styled(Stack)`
  gap: 12px;
  padding: 16px;
`;

const ItemCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
`;

const MetadataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const MetadataLabel = styled(Text)`
  font-size: 12px;
  color: ${inkLight};
  font-weight: 400;
  min-width: 80px;
`;

const MetadataValue = styled(Text)`
  font-size: 12px;
  color: ${inkDark};
  font-weight: 500;
  text-align: right;
`;

const EmptyState = styled(Stack)`
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 12px;
`;

const RiskScoreBar = styled.div`
  position: relative;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  flex: 1;
  max-width: 80px;
`;

const RiskScoreFill = styled.div<{ score: number; color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => (props.score / 10) * 100}%;
  background: ${(props) => props.color};
  border-radius: 2px;
  transition: width 0.3s ease;
`;
